import { useState, useEffect } from "react";
import { Cloud, CloudRain, Sun, Wind, Droplets, AlertTriangle, Thermometer, CloudLightning, Snowflake, Info, CloudSun, Calendar, TrendingUp, BarChart3 } from "lucide-react";
import { Card } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import axios from "axios";
import PrecipitationChart from "./PrecipitationChart";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Hydrological data from CIAGC, MITECO, SEPREM, iagua.es and official sources
// Formula: Volumen aportado ≈ lluvia (m) × cuenca (km²) × coeficiente de escorrentía
// Runoff coefficient for Gran Canaria volcanic terrain with steep ravines: 0.35-0.50
// Data verified from: Plan Hidrológico Gran Canaria, Consejo Insular de Aguas
// DATOS REALES CONSOLIDADOS - 26/03/2026
const DAM_HYDROLOGY = {
  PARRALILLO: { 
    basin_km2: 66,           // Cuenca del Barranco del Parralillo/Molino (oficial)
    runoff_coef: 0.45,       // Coef. zona alta, respuesta rápida
    capacity_hm3: 5.58,      // Máximo: 5,576,719 m³
    rebose_hm3: 4.57,        // Rebose: 4,569,808 m³ a 53.00m
    maximo_m: 56.70,
    rebose_m: 53.00,
    minimo_m: 20.56,
    surface_m2: 210000,      // Superficie embalse NMN: 21 hectáreas
    terrain: "Gravedad recta, cabecera del sistema, alta escorrentía"
  },
  SIBERIO: { 
    basin_km2: 29,           // Cuenca del Barranco de Siberio (oficial)
    runoff_coef: 0.45,       // Coef. medio terreno volcánico barrancos
    capacity_hm3: 4.50,      // Estimado máximo
    rebose_hm3: 4.36,        // Rebose: 4,356,364 m³ a 74.00m
    maximo_m: 74.45,
    rebose_m: 74.00,
    minimo_m: 17.00,
    surface_m2: 190000,      // Superficie embalse NMN: 19 hectáreas
    terrain: "Escollera con pantalla asfáltica, barrancos encajados"
  },
  CAIDERO: { 
    basin_km2: 104,          // Cuenca total Barranco de La Aldea
    runoff_coef: 0.40,       // Coef. zona baja, más infiltración
    capacity_hm3: 3.11,      // Máximo: 3,106,392 m³ a 46.60m
    rebose_hm3: 2.00,        // Rebose: 1,997,040 m³ a 40.00m
    maximo_m: 46.60,
    rebose_m: 40.00,
    minimo_m: 14.17,
    surface_m2: 1010000,     // Superficie embalse NMN: 101 hectáreas
    terrain: "Gravedad hormigón mamposteado, aguas abajo del sistema"
  },
  ARTENARA: { 
    basin_km2: 45,           // Cuenca estimada zona Artenara-Tejeda
    runoff_coef: 0.48,       // Coef. alto, zona montañosa
    capacity_hm3: 3.2,       // Capacidad estimada media
    rebose_hm3: 2.8,
    maximo_m: 55,
    rebose_m: 50,
    minimo_m: 10,
    surface_m2: 150000,      // Superficie estimada: 15 hectáreas
    terrain: "Zona alta montañosa, máxima precipitación de la isla"
  },
};

// Calculate water volume from precipitation using real runoff coefficients
// Volume (hm³) = Area (km²) × Precipitation (m) × Runoff coefficient
const calculateWaterVolume = (dam_key, precipitation_mm) => {
  const dam = DAM_HYDROLOGY[dam_key];
  if (!dam) return 0;
  
  const precip_m = precipitation_mm / 1000;
  const volume_hm3 = dam.basin_km2 * precip_m * dam.runoff_coef;
  return volume_hm3 * 1000000; // Return in m³
};

// Calculate expected height increase from water volume
// Height (m) = Volume (m³) / Surface area (m²)
const calculateHeightIncrease = (dam_key, volume_m3) => {
  const dam = DAM_HYDROLOGY[dam_key];
  if (!dam || !dam.surface_m2 || dam.surface_m2 === 0) return 0;
  
  return volume_m3 / dam.surface_m2; // Return in meters
};

const formatVolume = (volume_m3) => {
  if (volume_m3 >= 1000000) {
    return `${(volume_m3 / 1000000).toFixed(2)} hm³`;
  }
  // Show in m³ (metros cúbicos) for clarity
  return `${volume_m3.toLocaleString('es-ES', { maximumFractionDigits: 0 })} m³`;
};

// Convert volume to "azadas" - traditional Canarian water measure
// 1 azada = 10 l/s flow rate, commonly measured as 36 m³ (1 hour at 10 l/s)
const AZADA_M3 = 36; // 1 azada = 36 m³ (1 hora de agua a 10 l/s)

const formatVolumeWithAzadas = (volume_m3) => {
  const azadas = volume_m3 / AZADA_M3;
  return azadas.toFixed(0);
};

// Compact format for small widget - show in liters
const formatVolumeCompact = (volume_m3) => {
  const liters = volume_m3 * 1000; // Convert m³ to liters
  if (liters >= 1000000) {
    return `${(liters / 1000000).toFixed(1)}M L`;
  } else if (liters >= 1000) {
    return `${Math.round(liters / 1000).toLocaleString('es-ES')}k L`;
  }
  return `${Math.round(liters).toLocaleString('es-ES')} L`;
};

// AEMET weather codes to icons
const getWeatherIcon = (code) => {
  if (!code) return <Cloud className="w-5 h-5 text-gray-400" />;
  const codeStr = String(code).replace('n', '');
  const codeNum = parseInt(codeStr);
  
  // Despejado
  if (codeNum === 11) return <Sun className="w-5 h-5 text-yellow-400" />;
  // Poco nuboso / Intervalos nubosos
  if (codeNum >= 12 && codeNum <= 13) return <Cloud className="w-5 h-5 text-gray-300" />;
  // Nuboso
  if (codeNum >= 14 && codeNum <= 15) return <Cloud className="w-5 h-5 text-gray-400" />;
  // Cubierto
  if (codeNum === 16) return <Cloud className="w-5 h-5 text-gray-500" />;
  // Lluvia / chubascos
  if (codeNum >= 23 && codeNum <= 27) return <CloudRain className="w-5 h-5 text-blue-400" />;
  if (codeNum >= 43 && codeNum <= 46) return <CloudRain className="w-5 h-5 text-blue-400" />;
  // Nieve
  if (codeNum >= 33 && codeNum <= 36) return <Snowflake className="w-5 h-5 text-blue-200" />;
  // Tormenta
  if (codeNum >= 51 && codeNum <= 64) return <CloudLightning className="w-5 h-5 text-yellow-500" />;
  // Niebla / bruma
  if (codeNum >= 71 && codeNum <= 82) return <Cloud className="w-5 h-5 text-gray-400" />;
  
  return <Cloud className="w-5 h-5 text-gray-400" />;
};

const WeatherWidget = ({ fullscreen = false, vertical = false, prioritizeEstimates = false }) => {
  const [weather, setWeather] = useState(null);
  const [precipHistory, setPrecipHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPrecipChart, setShowPrecipChart] = useState(false);

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const [forecastRes, historyRes] = await Promise.all([
          axios.get(`${API_URL}/api/weather/forecast`),
          axios.get(`${API_URL}/api/weather/precipitation-history?days=30`)
        ]);
        setWeather(forecastRes.data);
        setPrecipHistory(historyRes.data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching AEMET weather:", err);
        setError("Error al obtener datos de AEMET");
        setLoading(false);
      }
    };

    fetchWeather();
    const interval = setInterval(fetchWeather, 30 * 60 * 1000); // Update every 30 min
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <Card className="bg-[#141415] border-zinc-800 p-3 h-full">
        <div className="animate-pulse flex flex-col gap-2">
          <div className="h-4 bg-zinc-800 rounded w-1/2"></div>
          <div className="h-8 bg-zinc-800 rounded"></div>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="bg-[#141415] border-zinc-800 p-3 h-full">
        <div className="text-red-400 text-xs">{error}</div>
      </Card>
    );
  }

  // Process AEMET data - use san_nicolas as primary location
  const aemetData = weather?.san_nicolas?.[0] || weather?.artenara?.[0] || weather?.tejeda?.[0];
  if (!aemetData) {
    return (
      <Card className="bg-[#141415] border-zinc-800 p-3 h-full">
        <div className="text-zinc-400 text-xs">Sin datos meteorológicos</div>
      </Card>
    );
  }

  const prediccion = aemetData.prediccion?.dia || [];
  const todayData = prediccion[0] || {};
  
  // Get current hour data
  const currentHour = new Date().getHours();
  const currentPeriodo = String(currentHour).padStart(2, '0');
  
  // Find current or closest temperature
  const temps = todayData.temperatura || [];
  const currentTemp = temps.find(t => t.periodo === currentPeriodo) || temps[temps.length - 1] || {};
  
  // Find current humidity
  const humidity = todayData.humedadRelativa || [];
  const currentHumidity = humidity.find(h => h.periodo === currentPeriodo) || humidity[humidity.length - 1] || {};
  
  // Get current sky state
  const skyStates = todayData.estadoCielo || [];
  const currentSky = skyStates.find(s => s.periodo === currentPeriodo) || skyStates[skyStates.length - 1] || {};
  
  // Get wind data
  const windData = todayData.vientoAndRachaMax || [];
  const currentWind = windData.find(w => w.periodo && w.periodo.includes(currentPeriodo)) || windData[0] || {};
  
  // Calculate precipitation for next 24 hours (today + part of tomorrow)
  let next24hPrecip = 0;
  const todayPrecip = todayData.precipitacion || [];
  todayPrecip.forEach(p => {
    const hora = parseInt(p.periodo);
    // Handle "Ip" (Inapreciable/trace) values from AEMET
    const val = p.value === 'Ip' ? 0.01 : parseFloat(p.value || 0);
    if (hora >= currentHour && !isNaN(val)) {
      next24hPrecip += val;
    }
  });
  
  // Add tomorrow's morning precipitation if available
  const tomorrowData = prediccion[1] || {};
  const tomorrowPrecip = tomorrowData.precipitacion || [];
  tomorrowPrecip.forEach(p => {
    const hora = parseInt(p.periodo);
    // Handle "Ip" (Inapreciable/trace) values from AEMET
    const val = p.value === 'Ip' ? 0.01 : parseFloat(p.value || 0);
    if (hora < currentHour && !isNaN(val)) {
      next24hPrecip += val;
    }
  });
  
  // Get probability of precipitation
  const probPrecip = todayData.probPrecipitacion || [];
  const maxPrecipProb = Math.max(...probPrecip.map(p => parseInt(p.value || 0)), 0);
  
  // Get probability of storm
  const probTormenta = todayData.probTormenta || [];
  const maxStormProb = Math.max(...probTormenta.map(p => parseInt(p.value || 0)), 0);
  
  // Calculate expected water for each reservoir using real runoff coefficients
  // Formula: Volumen aportado ≈ lluvia (m) × cuenca (km²) × coeficiente de escorrentía
  const expectedWater = {
    SIBERIO: calculateWaterVolume('SIBERIO', next24hPrecip),
    CAIDERO: calculateWaterVolume('CAIDERO', next24hPrecip),
    PARRALILLO: calculateWaterVolume('PARRALILLO', next24hPrecip),
    ARTENARA: calculateWaterVolume('ARTENARA', next24hPrecip),
  };
  
  // Calculate expected height increase for each reservoir
  const expectedHeight = {
    SIBERIO: calculateHeightIncrease('SIBERIO', expectedWater.SIBERIO),
    CAIDERO: calculateHeightIncrease('CAIDERO', expectedWater.CAIDERO),
    PARRALILLO: calculateHeightIncrease('PARRALILLO', expectedWater.PARRALILLO),
    ARTENARA: calculateHeightIncrease('ARTENARA', expectedWater.ARTENARA),
  };
  
  // Calculate average height increase
  const avgHeightIncrease = (expectedHeight.SIBERIO + expectedHeight.CAIDERO + expectedHeight.PARRALILLO + expectedHeight.ARTENARA) / 4;
  
  const totalExpectedWater = expectedWater.SIBERIO + expectedWater.CAIDERO + expectedWater.PARRALILLO + expectedWater.ARTENARA;
  
  // Debug: log the values
  console.log('WeatherWidget Debug:', { next24hPrecip, totalExpectedWater, expectedWater });

  // 7-day forecast
  const forecast7Days = prediccion.slice(0, 7).map(dia => {
    const precips = dia.precipitacion || [];
    // Handle "Ip" (Inapreciable/trace) values from AEMET
    const totalPrecip = precips.reduce((sum, p) => {
      const val = p.value === 'Ip' ? 0.01 : parseFloat(p.value || 0);
      return sum + (isNaN(val) ? 0 : val);
    }, 0);
    const diaTemps = dia.temperatura || [];
    const tempValues = diaTemps.map(t => parseInt(t.value || 0)).filter(t => t > 0);
    const minTemp = tempValues.length > 0 ? Math.min(...tempValues) : null;
    const maxTemp = tempValues.length > 0 ? Math.max(...tempValues) : null;
    const daySky = dia.estadoCielo || [];
    // Get midday sky state (around 12-14)
    const middaySky = daySky.find(s => ['12', '13', '14'].includes(s.periodo)) || daySky[Math.floor(daySky.length / 2)] || {};
    
    // Calcular agua esperada para este día por cada presa
    const dayWater = {
      PARRALILLO: calculateWaterVolume('PARRALILLO', totalPrecip),
      SIBERIO: calculateWaterVolume('SIBERIO', totalPrecip),
      CAIDERO: calculateWaterVolume('CAIDERO', totalPrecip),
    };
    const dayTotalWater = dayWater.PARRALILLO + dayWater.SIBERIO + dayWater.CAIDERO;
    const isSignificant = totalPrecip >= 0.05;
    
    return {
      fecha: dia.fecha,
      precip: totalPrecip,
      minTemp,
      maxTemp,
      skyCode: middaySky.value,
      skyDesc: middaySky.descripcion,
      water: dayWater,
      totalWater: dayTotalWater,
      isSignificant
    };
  });

  // Fullscreen version with more detailed information
  if (fullscreen) {
    return (
      <div className="bg-[#141415] h-full p-6 overflow-y-auto" data-testid="weather-widget-fullscreen">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="font-heading text-2xl uppercase tracking-wider text-zinc-100">
              AEMET - {aemetData.nombre}
            </h2>
            <p className="text-sm text-zinc-500 mt-1">{currentSky.descripcion}</p>
          </div>
          {getWeatherIcon(currentSky.value)}
        </div>
        
        {/* Current conditions - Large */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-zinc-900/50 rounded-lg p-4 text-center">
            <Thermometer className="w-6 h-6 text-orange-400 mx-auto mb-2" />
            <span className="text-3xl font-bold font-mono text-zinc-100">{currentTemp.value || '--'}°C</span>
            <p className="text-xs text-zinc-500 uppercase mt-1">Temperatura</p>
          </div>
          <div className="bg-zinc-900/50 rounded-lg p-4 text-center">
            <Droplets className="w-6 h-6 text-blue-400 mx-auto mb-2" />
            <span className="text-3xl font-bold font-mono text-zinc-100">{currentHumidity.value || '--'}%</span>
            <p className="text-xs text-zinc-500 uppercase mt-1">Humedad</p>
          </div>
          <div className="bg-zinc-900/50 rounded-lg p-4 text-center">
            <Wind className="w-6 h-6 text-cyan-400 mx-auto mb-2" />
            <span className="text-3xl font-bold font-mono text-zinc-100">
              {currentWind.velocidad ? currentWind.velocidad[0] : '--'} km/h
            </span>
            <p className="text-xs text-zinc-500 uppercase mt-1">Viento</p>
          </div>
        </div>
        
        {/* Precipitation forecast - Large */}
        <div className="bg-zinc-900/50 rounded-lg p-4 mb-6">
          <h3 className="text-base font-heading uppercase tracking-wider text-zinc-300 mb-3">Próximas 24 Horas</h3>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CloudRain className="w-10 h-10 text-blue-400" />
              <div>
                <span className="text-4xl font-bold text-zinc-100">{next24hPrecip.toFixed(1)} mm</span>
                <p className="text-sm text-zinc-500">Precipitación esperada</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="text-center">
                <CloudLightning className="w-5 h-5 text-yellow-400 mx-auto mb-1" />
                <span className="text-xl font-bold text-zinc-100">{maxStormProb}%</span>
                <p className="text-xs text-zinc-500">Tormenta</p>
              </div>
              <div className="text-center">
                <AlertTriangle className="w-5 h-5 text-amber-400 mx-auto mb-1" />
                <span className="text-xl font-bold text-zinc-100">{maxPrecipProb}%</span>
                <p className="text-xs text-zinc-500">Lluvia</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Expected water per reservoir - Large */}
        <div className="bg-zinc-900/50 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-heading uppercase tracking-wider text-zinc-300">Agua Esperada por Presa</h3>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Info className="w-4 h-4 text-zinc-500 hover:text-zinc-300" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs bg-zinc-800 text-zinc-100 border-zinc-700 p-3">
                  <p className="text-xs mb-2"><strong>Fórmula Volumen:</strong> Lluvia × Cuenca × Coef. escorrentía</p>
                  <p className="text-xs mb-2"><strong>Fórmula Altura:</strong> Volumen ÷ Superficie del embalse</p>
                  <p className="text-xs text-zinc-400">Datos CIAGC. Caidero recibe menos agua neta por regulación de presas superiores.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="grid grid-cols-4 gap-3">
            <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
              <span className="text-xs text-zinc-500 block mb-1">SIBERIO</span>
              <span className="text-lg font-bold font-mono text-blue-400">{formatVolume(expectedWater.SIBERIO)}</span>
              <span className="text-sm font-bold font-mono text-cyan-400 block">+{expectedHeight.SIBERIO.toFixed(2)} m</span>
              <p className="text-[10px] text-zinc-600 mt-1">Cuenca: {DAM_HYDROLOGY.SIBERIO.basin_km2} km² | Coef: {DAM_HYDROLOGY.SIBERIO.runoff_coef}</p>
            </div>
            <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
              <span className="text-xs text-zinc-500 block mb-1">CAIDERO</span>
              <span className="text-lg font-bold font-mono text-blue-400">{formatVolume(expectedWater.CAIDERO)}</span>
              <span className="text-sm font-bold font-mono text-cyan-400 block">+{expectedHeight.CAIDERO.toFixed(2)} m</span>
              <p className="text-[10px] text-zinc-600 mt-1">Cuenca: {DAM_HYDROLOGY.CAIDERO.basin_km2} km² | Coef: {DAM_HYDROLOGY.CAIDERO.runoff_coef}</p>
            </div>
            <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
              <span className="text-xs text-zinc-500 block mb-1">PARRALILLO</span>
              <span className="text-lg font-bold font-mono text-blue-400">{formatVolume(expectedWater.PARRALILLO)}</span>
              <span className="text-sm font-bold font-mono text-cyan-400 block">+{expectedHeight.PARRALILLO.toFixed(2)} m</span>
              <p className="text-[10px] text-zinc-600 mt-1">Cuenca: {DAM_HYDROLOGY.PARRALILLO.basin_km2} km² | Coef: {DAM_HYDROLOGY.PARRALILLO.runoff_coef}</p>
            </div>
            <div className="bg-blue-600/20 rounded-lg p-3 text-center border border-blue-500/30">
              <span className="text-xs text-blue-300 block mb-1">TOTAL</span>
              <span className="text-lg font-bold font-mono text-blue-300">{formatVolume(totalExpectedWater)}</span>
              <p className="text-[10px] text-blue-400/60 mt-1">Cuenca total: 199 km²</p>
            </div>
          </div>
        </div>
        
        {/* 7-day forecast - Large */}
        <div className="bg-zinc-900/50 rounded-lg p-4 pb-6">
          <h3 className="text-base font-heading uppercase tracking-wider text-zinc-300 mb-3">Pronóstico 7 Días</h3>
          <div className="grid grid-cols-7 gap-2">
            {forecast7Days.map((day, i) => (
              <div key={i} className="bg-zinc-800/50 rounded-lg p-2 text-center">
                <span className="text-xs text-zinc-400 block mb-1">
                  {i === 0 ? 'Hoy' : i === 1 ? 'Mañana' : day.fecha ? new Date(day.fecha).toLocaleDateString('es', { weekday: 'short' }).toUpperCase() : '---'}
                </span>
                <div className="flex items-center justify-center mb-1">
                  {getWeatherIcon(day.skyCode)}
                </div>
                <div className="flex items-center justify-center gap-0.5">
                  <CloudRain className="w-3 h-3 text-blue-400" />
                  <span className="text-sm font-bold font-mono text-zinc-100">{day.precip.toFixed(0)}</span>
                </div>
                {day.minTemp !== null && day.maxTemp !== null && (
                  <p className="text-[10px] text-zinc-500 mt-0.5">
                    {day.minTemp}°/{day.maxTemp}°
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Vertical layout for sidebar display - COMPACTO PARA MÓVIL
  if (vertical) {
    // Get weather icon based on current sky state
    const getCurrentWeatherIcon = (size = "w-4 h-4") => {
      if (!currentSky.value) return <Sun className={`${size} text-yellow-400`} />;
      const code = String(currentSky.value).replace('n', '');
      const codeNum = parseInt(code);
      
      if (codeNum === 11) return <Sun className={`${size} text-yellow-400`} />;
      if (codeNum >= 12 && codeNum <= 15) return <CloudSun className={`${size} text-gray-300`} />;
      if (codeNum === 16) return <Cloud className={`${size} text-gray-400`} />;
      if (codeNum >= 23 && codeNum <= 46) return <CloudRain className={`${size} text-blue-400`} />;
      if (codeNum >= 51 && codeNum <= 64) return <CloudLightning className={`${size} text-yellow-500`} />;
      return <Sun className={`${size} text-yellow-400`} />;
    };
    
    // Usar las mismas variables ya calculadas para consistencia
    const verticalDisplayPrecip = next24hPrecip < 0.01 ? '0.00' : next24hPrecip.toFixed(2);
    const verticalIsRainSignificant = next24hPrecip >= 0.05;
    const verticalEffectiveHeights = verticalIsRainSignificant ? expectedHeight : { PARRALILLO: 0, SIBERIO: 0, CAIDERO: 0 };
    const verticalEffectiveTotalWater = verticalIsRainSignificant ? totalExpectedWater : 0;
    
    return (
      <TooltipProvider>
        <Card className="bg-[#0A0F1A] border border-blue-900/50 p-2 h-full flex flex-col overflow-hidden rounded-lg" data-testid="weather-widget">
          {/* Header - TIEMPO ACTUAL */}
          <div className="flex items-center justify-between mb-2 pb-1 border-b border-blue-800/30">
            <div className="flex items-center gap-2">
              {getCurrentWeatherIcon("w-5 h-5")}
              <span className="font-bold text-sm uppercase tracking-wider text-blue-200">
                TIEMPO
              </span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button className="hover:bg-blue-800/30 rounded p-0.5 transition-colors">
                    <Info className="w-4 h-4 text-blue-400/50 hover:text-blue-300" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-[280px] bg-zinc-900 border-blue-800/50 p-3 text-left">
                  <div className="space-y-2">
                    <p className="text-xs font-bold text-blue-300 uppercase">Cálculo de agua prevista</p>
                    <p className="text-xs bg-zinc-800 px-2 py-1.5 rounded font-mono text-blue-200">
                      Volumen = Lluvia × Cuenca × Escorrentía
                    </p>
                    <div className="grid grid-cols-1 gap-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-emerald-400">PARRALILLO:</span>
                        <span className="text-zinc-400">{DAM_HYDROLOGY.PARRALILLO.basin_km2}km² × {DAM_HYDROLOGY.PARRALILLO.runoff_coef}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-amber-400">SIBERIO:</span>
                        <span className="text-zinc-400">{DAM_HYDROLOGY.SIBERIO.basin_km2}km² × {DAM_HYDROLOGY.SIBERIO.runoff_coef}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-cyan-400">CAIDERO:</span>
                        <span className="text-zinc-400">{DAM_HYDROLOGY.CAIDERO.basin_km2}km² × {DAM_HYDROLOGY.CAIDERO.runoff_coef}</span>
                      </div>
                    </div>
                    <p className="text-xs text-zinc-500 italic">
                      La escorrentía indica qué % del agua llega al embalse por los barrancos.
                    </p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-lg font-bold text-white">{currentTemp.value || '--'}°C</span>
            </div>
          </div>
          
          {/* Estado actual del tiempo */}
          <div className="flex items-center justify-between bg-gradient-to-r from-blue-900/30 to-transparent rounded px-2 py-1.5 mb-2">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-blue-600/30 rounded">
                {getCurrentWeatherIcon("w-6 h-6")}
              </div>
              <div>
                <span className="text-sm text-zinc-300 block font-medium">{currentSky.descripcion || 'Despejado'}</span>
                <div className="flex items-center gap-3 text-xs text-zinc-500">
                  <span className="flex items-center gap-1"><Wind className="w-3 h-3" /> {currentWind.velocidad?.[0] || '--'} km/h</span>
                  <span className="flex items-center gap-1"><Droplets className="w-3 h-3" /> {currentHumidity.value || '--'}%</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Previsión agua - título */}
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs uppercase tracking-wider text-zinc-500 font-medium">Lluvia 24h</span>
            <div className="flex items-center gap-1 bg-blue-600 px-2 py-1 rounded">
              <CloudRain className="w-3 h-3 text-white" />
              <span className="text-sm font-bold text-white">{verticalDisplayPrecip}mm</span>
            </div>
          </div>
          
          {/* Previsión 7 días - MEJORADO con tooltips */}
          <div className="flex gap-1 mb-2 overflow-x-auto">
            {forecast7Days.slice(0, 7).map((day, i) => (
              <Tooltip key={i}>
                <TooltipTrigger asChild>
                  <div className={`flex-1 min-w-[38px] rounded p-1 text-center cursor-help transition-colors ${day.isSignificant ? 'bg-blue-900/40 hover:bg-blue-800/50' : 'bg-[#0D1520] hover:bg-[#111a28]'}`}>
                    <span className="text-[10px] text-zinc-400 block font-medium">
                      {i === 0 ? 'HOY' : day.fecha ? new Date(day.fecha).toLocaleDateString('es', { weekday: 'short' }).substring(0, 2).toUpperCase() : '-'}
                    </span>
                    <div className="flex justify-center my-1">
                      {(() => {
                        if (!day.skyCode) return <Sun className="w-5 h-5 text-yellow-400" />;
                        const code = String(day.skyCode).replace('n', '');
                        const codeNum = parseInt(code);
                        if (codeNum === 11) return <Sun className="w-5 h-5 text-yellow-400" />;
                        if (codeNum >= 12 && codeNum <= 15) return <CloudSun className="w-5 h-5 text-gray-300" />;
                        if (codeNum === 16) return <Cloud className="w-5 h-5 text-gray-400" />;
                        if (codeNum >= 23 && codeNum <= 46) return <CloudRain className="w-5 h-5 text-blue-400" />;
                        return <Sun className="w-5 h-5 text-yellow-400" />;
                      })()}
                    </div>
                    {/* Temperaturas min/max */}
                    <div className="text-[10px] text-zinc-400 font-medium">
                      <span className="text-blue-300">{day.minTemp || '--'}°</span>
                      <span className="mx-0.5 text-zinc-600">/</span>
                      <span className="text-orange-300">{day.maxTemp || '--'}°</span>
                    </div>
                    {/* Lluvia del día */}
                    {day.precip > 0 && (
                      <div className="flex items-center justify-center gap-0.5 mt-1">
                        <CloudRain className="w-3 h-3 text-blue-400" />
                        <span className="text-[10px] font-mono text-blue-300">{day.precip.toFixed(1)}</span>
                      </div>
                    )}
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[240px] bg-zinc-900 border-blue-800/50 p-3">
                  <div className="space-y-2">
                    {/* Fecha */}
                    <p className="text-sm font-bold text-blue-300 uppercase border-b border-zinc-700 pb-1">
                      {i === 0 ? 'HOY' : day.fecha ? new Date(day.fecha).toLocaleDateString('es', { weekday: 'long', day: 'numeric', month: 'short' }) : '---'}
                    </p>
                    
                    {/* Descripción del cielo */}
                    <p className="text-xs text-zinc-400">{day.skyDesc || 'Despejado'}</p>
                    
                    {/* Temperaturas */}
                    <div className="flex justify-between text-xs">
                      <span className="text-zinc-500">Temperatura:</span>
                      <span>
                        <span className="text-blue-300">{day.minTemp || '--'}°</span>
                        <span className="text-zinc-600 mx-1">→</span>
                        <span className="text-orange-300">{day.maxTemp || '--'}°</span>
                      </span>
                    </div>
                    
                    {/* Precipitación */}
                    <div className="flex justify-between text-xs">
                      <span className="text-zinc-500">Precipitación:</span>
                      <span className={day.isSignificant ? 'text-blue-300 font-bold' : 'text-zinc-400'}>
                        {day.precip.toFixed(2)} mm
                      </span>
                    </div>
                    
                    {/* Agua esperada - solo si hay lluvia significativa */}
                    {day.isSignificant ? (
                      <div className="mt-2 pt-2 border-t border-zinc-700 space-y-1">
                        <p className="text-xs text-zinc-500 uppercase">Agua esperada en embalses:</p>
                        <div className="grid grid-cols-1 gap-1 text-xs">
                          <div className="flex justify-between bg-zinc-800/50 px-2 py-1 rounded">
                            <span className="text-emerald-400">PARRALILLO:</span>
                            <span className="text-zinc-200 font-mono">{formatVolume(day.water.PARRALILLO)}</span>
                          </div>
                          <div className="flex justify-between bg-zinc-800/50 px-1 py-0.5 rounded">
                            <span className="text-amber-400">SIBERIO:</span>
                            <span className="text-zinc-200 font-mono">{formatVolume(day.water.SIBERIO)}</span>
                          </div>
                          <div className="flex justify-between bg-zinc-800/50 px-1 py-0.5 rounded">
                            <span className="text-cyan-400">CAIDERO:</span>
                            <span className="text-zinc-200 font-mono">{formatVolume(day.water.CAIDERO)}</span>
                          </div>
                          <div className="flex justify-between bg-blue-600/30 px-1 py-0.5 rounded border border-blue-500/30 mt-0.5">
                            <span className="text-blue-200 font-bold">TOTAL:</span>
                            <span className="text-white font-mono font-bold">{formatVolume(day.totalWater)}</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-[7px] text-zinc-600 italic mt-1">Sin aporte significativo de agua</p>
                    )}
                  </div>
                </TooltipContent>
              </Tooltip>
            ))}
          </div>
          
          {/* Histórico de lluvias 30 días */}
          {precipHistory && (
            <Tooltip>
              <TooltipTrigger asChild>
                <div 
                  onClick={() => setShowPrecipChart(true)}
                  className="bg-gradient-to-r from-teal-900/30 to-blue-900/20 rounded px-2 py-2 mb-2 cursor-pointer hover:from-teal-800/40 hover:to-blue-800/30 transition-colors border border-teal-800/30"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-teal-400" />
                      <span className="text-xs uppercase tracking-wider text-teal-300/80 font-semibold">Últimos 30 días</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-teal-400/60" />
                      <CloudRain className="w-4 h-4 text-teal-400" />
                      <span className="text-sm font-bold font-mono text-teal-300">{precipHistory.total_precipitation_mm}mm</span>
                    </div>
                  </div>
                  {/* Resumen por presa */}
                  {precipHistory.water_by_dam && (
                    <div className="grid grid-cols-3 gap-1 mt-2 text-xs">
                      <div className="bg-emerald-900/30 rounded px-2 py-1 text-center">
                        <span className="text-emerald-400 font-bold">PAR</span>
                        <span className="text-zinc-300 block font-mono text-sm">{formatVolume(precipHistory.water_by_dam.PARRALILLO?.water_m3 || 0)}</span>
                      </div>
                      <div className="bg-amber-900/30 rounded px-2 py-1 text-center">
                        <span className="text-amber-400 font-bold">SIB</span>
                        <span className="text-zinc-300 block font-mono text-sm">{formatVolume(precipHistory.water_by_dam.SIBERIO?.water_m3 || 0)}</span>
                      </div>
                      <div className="bg-cyan-900/30 rounded px-2 py-1 text-center">
                        <span className="text-cyan-400 font-bold">CAI</span>
                        <span className="text-zinc-300 block font-mono text-sm">{formatVolume(precipHistory.water_by_dam.CAIDERO?.water_m3 || 0)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-[320px] bg-zinc-900 border-teal-800/50 p-3">
                <div className="space-y-2">
                  <div className="flex items-center justify-between border-b border-zinc-700 pb-2">
                    <p className="text-sm font-bold text-teal-300 uppercase">
                      Histórico de Precipitaciones (30 días)
                    </p>
                    <span className="text-xs text-teal-400 bg-teal-900/40 px-2 py-1 rounded">Click para gráfica</span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Total acumulado:</span>
                      <span className="text-teal-300 font-bold">{precipHistory.total_precipitation_mm} mm</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Días con lluvia:</span>
                      <span className="text-blue-300">{precipHistory.rainy_days}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Días secos:</span>
                      <span className="text-amber-300">{precipHistory.dry_days}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Media diaria:</span>
                      <span className="text-zinc-300">{precipHistory.avg_daily_mm} mm</span>
                    </div>
                  </div>
                  
                  {precipHistory.max_precipitation_mm > 0 && (
                    <div className="bg-blue-900/30 rounded px-2 py-1 mt-1">
                      <p className="text-[7px] text-zinc-500">Máxima registrada:</p>
                      <p className="text-[9px] text-blue-300 font-bold">
                        {precipHistory.max_precipitation_mm} mm 
                        <span className="text-zinc-500 font-normal ml-1">
                          ({precipHistory.max_precipitation_date ? new Date(precipHistory.max_precipitation_date).toLocaleDateString('es', { day: 'numeric', month: 'short' }) : '-'})
                        </span>
                      </p>
                    </div>
                  )}
                  
                  {/* Desglose por presa */}
                  {precipHistory.water_by_dam && (
                    <div className="mt-2 pt-2 border-t border-zinc-700">
                      <p className="text-[7px] text-zinc-500 uppercase mb-1.5">Agua aportada por embalse:</p>
                      <div className="space-y-1">
                        <div className="flex justify-between items-center bg-emerald-900/20 rounded px-2 py-1 border border-emerald-800/30">
                          <div>
                            <span className="text-[8px] text-emerald-400 font-bold">PARRALILLO</span>
                            <span className="text-[6px] text-zinc-600 ml-1">(Bco. del Parralillo)</span>
                          </div>
                          <span className="text-[9px] text-emerald-300 font-mono font-bold">{formatVolume(precipHistory.water_by_dam.PARRALILLO?.water_m3 || 0)}</span>
                        </div>
                        <div className="flex justify-between items-center bg-amber-900/20 rounded px-2 py-1 border border-amber-800/30">
                          <div>
                            <span className="text-[8px] text-amber-400 font-bold">SIBERIO</span>
                            <span className="text-[6px] text-zinc-600 ml-1">(Bco. de Siberio)</span>
                          </div>
                          <span className="text-[9px] text-amber-300 font-mono font-bold">{formatVolume(precipHistory.water_by_dam.SIBERIO?.water_m3 || 0)}</span>
                        </div>
                        <div className="flex justify-between items-center bg-cyan-900/20 rounded px-2 py-1 border border-cyan-800/30">
                          <div>
                            <span className="text-[8px] text-cyan-400 font-bold">CAIDERO</span>
                            <span className="text-[6px] text-zinc-600 ml-1">(Bco. de La Aldea)</span>
                          </div>
                          <span className="text-[9px] text-cyan-300 font-mono font-bold">{formatVolume(precipHistory.water_by_dam.CAIDERO?.water_m3 || 0)}</span>
                        </div>
                        <div className="flex justify-between items-center bg-teal-600/30 rounded px-2 py-1.5 border border-teal-500/40 mt-1">
                          <span className="text-[9px] text-white font-bold">TOTAL 3 EMBALSES</span>
                          <span className="text-[10px] text-teal-200 font-mono font-black">{formatVolume(precipHistory.total_water_m3)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Mini gráfico de barras de los últimos días */}
                  <div className="mt-1.5 pt-1.5 border-t border-zinc-700">
                    <p className="text-[7px] text-zinc-500 mb-1">Últimos 14 días:</p>
                    <div className="flex items-end gap-px h-6">
                      {precipHistory.daily_data?.slice(0, 14).reverse().map((day, idx) => {
                        const maxPrecip = Math.max(...precipHistory.daily_data.slice(0, 14).map(d => d.precipitation_mm), 1);
                        const height = day.precipitation_mm > 0 ? Math.max((day.precipitation_mm / maxPrecip) * 100, 10) : 0;
                        return (
                          <div 
                            key={idx} 
                            className="flex-1 bg-blue-500/60 rounded-t transition-all"
                            style={{ height: `${height}%`, minHeight: day.precipitation_mm > 0 ? '2px' : '0' }}
                            title={`${day.date}: ${day.precipitation_mm}mm`}
                          />
                        );
                      })}
                    </div>
                  </div>
                </div>
              </TooltipContent>
            </Tooltip>
          )}
          
          {/* Histórico 30 días - Compacto con click para gráfica (Layout Vertical) */}
          {precipHistory && (
            <div 
              data-testid="precip-history-30d-vertical"
              onClick={() => setShowPrecipChart(true)}
              className="bg-gradient-to-r from-teal-900/30 to-blue-900/20 rounded px-1.5 py-1 mb-1 cursor-pointer hover:from-teal-800/40 hover:to-blue-800/30 transition-colors border border-teal-800/30"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <Calendar className="w-2.5 h-2.5 text-teal-400" />
                  <span className="text-[7px] uppercase tracking-wider text-teal-300/80 font-semibold">30 días</span>
                </div>
                <div className="flex items-center gap-1">
                  <BarChart3 className="w-2.5 h-2.5 text-teal-400/60" />
                  <span className="text-[8px] font-bold font-mono text-teal-300">{precipHistory.total_precipitation_mm}mm</span>
                </div>
              </div>
              {/* Resumen por presa */}
              {precipHistory.water_by_dam && (
                <div className="grid grid-cols-3 gap-0.5 mt-0.5 text-[6px]">
                  <div className="bg-emerald-900/30 rounded px-0.5 py-0.5 text-center">
                    <span className="text-emerald-400 font-bold">PAR</span>
                    <span className="text-zinc-400 block font-mono text-[5px]">{formatVolume(precipHistory.water_by_dam.PARRALILLO?.water_m3 || 0)}</span>
                  </div>
                  <div className="bg-amber-900/30 rounded px-0.5 py-0.5 text-center">
                    <span className="text-amber-400 font-bold">SIB</span>
                    <span className="text-zinc-400 block font-mono text-[5px]">{formatVolume(precipHistory.water_by_dam.SIBERIO?.water_m3 || 0)}</span>
                  </div>
                  <div className="bg-cyan-900/30 rounded px-0.5 py-0.5 text-center">
                    <span className="text-cyan-400 font-bold">CAI</span>
                    <span className="text-zinc-400 block font-mono text-[5px]">{formatVolume(precipHistory.water_by_dam.CAIDERO?.water_m3 || 0)}</span>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Las 3 presas - COMPACTO - Solo mostrar si hay lluvia significativa */}
          {verticalIsRainSignificant ? (
            <div className="flex-1 flex flex-col justify-center space-y-0.5 min-h-0 overflow-hidden">
              <span className="text-[7px] text-zinc-500 mb-0.5">Aumento nivel previsto:</span>
              {/* PARRALILLO */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex justify-between items-center bg-[#0D1520] rounded px-1.5 py-0.5 cursor-help hover:bg-[#111a28] transition-colors">
                    <span className="text-[8px] text-zinc-300 font-semibold">PARRALILLO</span>
                    <span className="text-[11px] font-mono font-bold text-emerald-400">+{verticalEffectiveHeights.PARRALILLO.toFixed(2)}<span className="text-[7px]">m</span></span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="left" className="bg-zinc-900 border-emerald-800/50 p-2">
                  <div className="text-[8px] space-y-0.5">
                    <p className="font-bold text-emerald-400">Bco. Parralillo</p>
                    <p className="text-zinc-300">{verticalDisplayPrecip}mm × {DAM_HYDROLOGY.PARRALILLO.basin_km2}km² × {DAM_HYDROLOGY.PARRALILLO.runoff_coef}</p>
                    <p className="text-zinc-100 font-mono">= {formatVolume(expectedWater.PARRALILLO)}</p>
                  </div>
                </TooltipContent>
              </Tooltip>
              
              {/* SIBERIO */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex justify-between items-center bg-[#0D1520] rounded px-1.5 py-0.5 cursor-help hover:bg-[#111a28] transition-colors">
                    <span className="text-[8px] text-zinc-300 font-semibold">SIBERIO</span>
                    <span className="text-[11px] font-mono font-bold text-amber-400">+{verticalEffectiveHeights.SIBERIO.toFixed(2)}<span className="text-[7px]">m</span></span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="left" className="bg-zinc-900 border-amber-800/50 p-2">
                  <div className="text-[8px] space-y-0.5">
                    <p className="font-bold text-amber-400">Bco. Siberio</p>
                    <p className="text-zinc-300">{verticalDisplayPrecip}mm × {DAM_HYDROLOGY.SIBERIO.basin_km2}km² × {DAM_HYDROLOGY.SIBERIO.runoff_coef}</p>
                    <p className="text-zinc-100 font-mono">= {formatVolume(expectedWater.SIBERIO)}</p>
                  </div>
                </TooltipContent>
              </Tooltip>
              
              {/* CAIDERO */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex justify-between items-center bg-[#0D1520] rounded px-1.5 py-0.5 cursor-help hover:bg-[#111a28] transition-colors">
                    <span className="text-[8px] text-zinc-300 font-semibold">CAIDERO</span>
                    <span className="text-[11px] font-mono font-bold text-cyan-400">+{verticalEffectiveHeights.CAIDERO.toFixed(2)}<span className="text-[7px]">m</span></span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="left" className="bg-zinc-900 border-cyan-800/50 p-2">
                  <div className="text-[8px] space-y-0.5">
                    <p className="font-bold text-cyan-400">Bco. La Aldea</p>
                    <p className="text-zinc-300">{verticalDisplayPrecip}mm × {DAM_HYDROLOGY.CAIDERO.basin_km2}km² × {DAM_HYDROLOGY.CAIDERO.runoff_coef}</p>
                    <p className="text-zinc-100 font-mono">= {formatVolume(expectedWater.CAIDERO)}</p>
                  </div>
                </TooltipContent>
              </Tooltip>
              
              {/* TOTAL AGUA */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex justify-between items-center bg-blue-600 rounded px-1.5 py-1 cursor-help hover:bg-blue-500 transition-colors">
                    <span className="text-[9px] text-white font-bold">AGUA TOTAL</span>
                    <span className="text-sm font-mono font-black text-white">{formatVolume(verticalEffectiveTotalWater)}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="left" className="bg-zinc-900 border-blue-500/50 p-2">
                  <div className="text-[8px] space-y-0.5">
                    <p className="font-bold text-blue-300">Suma total (3 cuencas)</p>
                    <p className="text-zinc-300">PARRALILLO + SIBERIO + CAIDERO</p>
                    <p className="text-zinc-100 font-mono font-bold">= {formatVolume(totalExpectedWater)}</p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center p-2">
                <Sun className="w-8 h-8 text-yellow-400 mx-auto mb-1" />
                <span className="text-[10px] text-zinc-400 font-medium">Sin lluvia significativa</span>
                {next24hPrecip > 0 && (
                  <span className="text-[8px] text-zinc-600 block">({verticalDisplayPrecip}mm - insignificante)</span>
                )}
              </div>
            </div>
          )}
        </Card>
        
        {/* Modal de Gráfica de Precipitaciones */}
        <PrecipitationChart isOpen={showPrecipChart} onClose={() => setShowPrecipChart(false)} />
      </TooltipProvider>
    );
  }

  // Determinar si la lluvia es significativa (umbral: 0.05mm)
  const isRainSignificant = next24hPrecip >= 0.05;
  const displayPrecip = next24hPrecip < 0.01 ? '0.00' : next24hPrecip.toFixed(2);
  
  // Calcular agua efectiva solo si la lluvia es significativa
  const effectiveTotalWater = isRainSignificant ? totalExpectedWater : 0;
  const effectiveHeights = isRainSignificant ? expectedHeight : { PARRALILLO: 0, SIBERIO: 0, CAIDERO: 0 };

  // Normal compact version - PREVISIÓN AGUA 24H CON LAS 3 PRESAS Y TOTAL
  return (
    <TooltipProvider>
      <Card className="bg-gradient-to-b from-[#050810] to-[#0a0f18] border border-blue-900/30 p-1 sm:p-2 h-full flex flex-col overflow-hidden rounded-lg" data-testid="weather-widget">
        {/* Header - PREVISIÓN AGUA 24H */}
        <div className="flex items-center justify-between mb-0.5 pb-0.5 border-b border-blue-800/20">
          <div className="flex items-center gap-1">
            <span className="font-bold text-[7px] sm:text-[10px] uppercase tracking-wider text-blue-300/80">
              PREVISIÓN 24H
            </span>
            <Tooltip>
              <TooltipTrigger asChild>
                <button className="hover:bg-blue-800/30 rounded p-0.5 transition-colors">
                  <Info className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-blue-400/60 hover:text-blue-300" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-[280px] bg-zinc-900 border-blue-800/50 p-3 text-left">
                <div className="space-y-2">
                  <p className="text-[10px] font-bold text-blue-300 uppercase tracking-wide">¿Cómo se calcula?</p>
                  <div className="text-[9px] text-zinc-300 space-y-1.5">
                    <p className="font-semibold text-zinc-100">Fórmula del volumen de agua:</p>
                    <p className="bg-zinc-800 px-2 py-1 rounded font-mono text-blue-200">
                      Volumen = Lluvia × Cuenca × Coef. Escorrentía
                    </p>
                    <p className="text-zinc-400 mt-2">Datos de las cuencas hidrográficas:</p>
                    <div className="grid grid-cols-1 gap-1 text-[8px]">
                      <div className="flex justify-between bg-zinc-800/50 px-1.5 py-0.5 rounded">
                        <span className="text-emerald-400">PARRALILLO:</span>
                        <span className="text-zinc-300">{DAM_HYDROLOGY.PARRALILLO.basin_km2} km² | Coef: {DAM_HYDROLOGY.PARRALILLO.runoff_coef}</span>
                      </div>
                      <div className="flex justify-between bg-zinc-800/50 px-1.5 py-0.5 rounded">
                        <span className="text-amber-400">SIBERIO:</span>
                        <span className="text-zinc-300">{DAM_HYDROLOGY.SIBERIO.basin_km2} km² | Coef: {DAM_HYDROLOGY.SIBERIO.runoff_coef}</span>
                      </div>
                      <div className="flex justify-between bg-zinc-800/50 px-1.5 py-0.5 rounded">
                        <span className="text-cyan-400">CAIDERO:</span>
                        <span className="text-zinc-300">{DAM_HYDROLOGY.CAIDERO.basin_km2} km² | Coef: {DAM_HYDROLOGY.CAIDERO.runoff_coef}</span>
                      </div>
                    </div>
                    <p className="text-zinc-500 mt-1.5 text-[8px] italic">
                      El coeficiente de escorrentía indica qué porcentaje del agua de lluvia llega al embalse a través de los barrancos. En Gran Canaria oscila entre 0.35-0.50 por el terreno volcánico.
                    </p>
                    <p className="text-zinc-500 text-[8px]">
                      Cuenca total: <span className="text-blue-300 font-semibold">199 km²</span> (suma de los 3 barrancos)
                    </p>
                    {!isRainSignificant && next24hPrecip > 0 && (
                      <p className="text-yellow-500 text-[8px] mt-1 bg-yellow-900/20 px-1.5 py-1 rounded">
                        ⚠️ Lluvia &lt;0.05mm se considera insignificante y no genera aporte medible.
                      </p>
                    )}
                  </div>
                </div>
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="flex items-center gap-0.5 bg-blue-600 px-1 py-0.5 rounded">
            <CloudRain className="w-2 h-2 sm:w-2.5 sm:h-2.5 text-white" />
            <span className="text-[7px] sm:text-[10px] font-bold text-white">{displayPrecip}mm</span>
          </div>
        </div>
        
        {/* Las 3 presas - ultra compacto */}
        {isRainSignificant ? (
          <div className="flex-1 flex flex-col justify-center space-y-0.5">
            {/* PARRALILLO */}
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex justify-between items-center bg-[#0a1018] rounded px-1 py-0.5 cursor-help hover:bg-[#0d1520] transition-colors">
                  <span className="text-[7px] sm:text-[10px] text-zinc-300 font-medium">PARRALILLO</span>
                  <span className="text-[9px] sm:text-sm font-mono font-bold text-emerald-400">+{effectiveHeights.PARRALILLO.toFixed(2)}<span className="text-[6px] sm:text-[8px] text-emerald-400/60">m</span></span>
                </div>
              </TooltipTrigger>
              <TooltipContent side="left" className="bg-zinc-900 border-emerald-800/50 p-2">
                <div className="text-[9px] space-y-1">
                  <p className="font-bold text-emerald-400">Bco. del Parralillo</p>
                  <p className="text-zinc-300">{displayPrecip}mm × {DAM_HYDROLOGY.PARRALILLO.basin_km2}km² × {DAM_HYDROLOGY.PARRALILLO.runoff_coef}</p>
                  <p className="text-zinc-100 font-mono">= {formatVolume(expectedWater.PARRALILLO)}</p>
                </div>
              </TooltipContent>
            </Tooltip>
            
            {/* SIBERIO */}
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex justify-between items-center bg-[#0a1018] rounded px-1 py-0.5 cursor-help hover:bg-[#0d1520] transition-colors">
                  <span className="text-[7px] sm:text-[10px] text-zinc-300 font-medium">SIBERIO</span>
                  <span className="text-[9px] sm:text-sm font-mono font-bold text-amber-400">+{effectiveHeights.SIBERIO.toFixed(2)}<span className="text-[6px] sm:text-[8px] text-amber-400/60">m</span></span>
                </div>
              </TooltipTrigger>
              <TooltipContent side="left" className="bg-zinc-900 border-amber-800/50 p-2">
                <div className="text-[9px] space-y-1">
                  <p className="font-bold text-amber-400">Bco. de Siberio</p>
                  <p className="text-zinc-300">{displayPrecip}mm × {DAM_HYDROLOGY.SIBERIO.basin_km2}km² × {DAM_HYDROLOGY.SIBERIO.runoff_coef}</p>
                  <p className="text-zinc-100 font-mono">= {formatVolume(expectedWater.SIBERIO)}</p>
                </div>
              </TooltipContent>
            </Tooltip>
            
            {/* CAIDERO */}
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex justify-between items-center bg-[#0a1018] rounded px-1 py-0.5 cursor-help hover:bg-[#0d1520] transition-colors">
                  <span className="text-[7px] sm:text-[10px] text-zinc-300 font-medium">CAIDERO</span>
                  <span className="text-[9px] sm:text-sm font-mono font-bold text-cyan-400">+{effectiveHeights.CAIDERO.toFixed(2)}<span className="text-[6px] sm:text-[8px] text-cyan-400/60">m</span></span>
                </div>
              </TooltipTrigger>
              <TooltipContent side="left" className="bg-zinc-900 border-cyan-800/50 p-2">
                <div className="text-[9px] space-y-1">
                  <p className="font-bold text-cyan-400">Bco. de La Aldea (Caidero)</p>
                  <p className="text-zinc-300">{displayPrecip}mm × {DAM_HYDROLOGY.CAIDERO.basin_km2}km² × {DAM_HYDROLOGY.CAIDERO.runoff_coef}</p>
                  <p className="text-zinc-100 font-mono">= {formatVolume(expectedWater.CAIDERO)}</p>
                </div>
              </TooltipContent>
            </Tooltip>
            
            {/* TOTAL */}
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex justify-between items-center bg-blue-600 rounded px-1 py-0.5 cursor-help hover:bg-blue-500 transition-colors">
                  <span className="text-[7px] sm:text-[10px] text-white font-bold">TOTAL</span>
                  <span className="text-[10px] sm:text-base font-mono font-black text-white">{formatVolume(effectiveTotalWater)}</span>
                </div>
              </TooltipTrigger>
              <TooltipContent side="left" className="bg-zinc-900 border-blue-500/50 p-2">
                <div className="text-[9px] space-y-1">
                  <p className="font-bold text-blue-300">Suma de las 3 cuencas</p>
                  <p className="text-zinc-300">Cuenca total: 199 km²</p>
                  <p className="text-zinc-400">PARRALILLO + SIBERIO + CAIDERO</p>
                  <p className="text-zinc-100 font-mono font-bold">= {formatVolume(totalExpectedWater)}</p>
                </div>
              </TooltipContent>
            </Tooltip>
          </div>
        ) : (
          <div className="flex-1 flex flex-col justify-center items-center py-2">
            <Sun className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-400 mb-1" />
            <span className="text-[8px] sm:text-[10px] text-zinc-400 font-medium text-center">Sin lluvia significativa</span>
            {next24hPrecip > 0 && (
              <span className="text-[7px] sm:text-[8px] text-zinc-600 text-center">({displayPrecip}mm - insignificante)</span>
            )}
          </div>
        )}
        
        {/* Footer */}
        <div className="flex items-center justify-between mt-0.5 pt-0.5 border-t border-blue-900/20">
          <div className="flex items-center gap-0.5">
            <Thermometer className="w-2 h-2 text-orange-400" />
            <span className="text-[7px] sm:text-[10px] font-mono font-semibold text-zinc-100">{currentTemp.value || '--'}°</span>
          </div>
          <div className="flex items-center gap-0.5">
            <Droplets className="w-2 h-2 text-blue-400" />
            <span className="text-[7px] sm:text-[10px] font-mono text-zinc-300">{currentHumidity.value || '--'}%</span>
          </div>
        </div>
      </Card>
      
      {/* Modal de Gráfica de Precipitaciones */}
      <PrecipitationChart isOpen={showPrecipChart} onClose={() => setShowPrecipChart(false)} />
    </TooltipProvider>
  );
};

export default WeatherWidget;
